import {createContext, useContext, useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'

import {useOnAppStateChange} from '#/lib/appState'
import * as persisted from '#/state/persisted'
import {useTickEveryMinute} from '#/state/shell/tick-every-minute'
import {ANNOUNCEMENTS_URL} from '#/env'
import {
  type Announcement,
  type AnnouncementsConfig,
} from '#/features/announcements/types'

// Shown when EXPO_PUBLIC_ANNOUNCEMENTS_URL is not configured.
const FALLBACK_ANNOUNCEMENTS: AnnouncementsConfig = {
  announcements: [
    {
      id: 'w-live-climate-week-nyc-2026',
      showFrom: '2026-10-01T17:30:00Z', // 19:30 CEST
      showUntil: '2026-10-01T19:00:00Z', // 21:00 CEST
      width: '400px',
      height: '559px',
      // ponytail: require() resolves at bundle time; fine for hardcoded fallback
      image: require('../../../assets/images/live_climate_week_announcement.png'),
      imageAspectRatio: 1878 / 932,
      preHeader: 'A Climate Week NYC special on W Live',
      header: "It's not paranoia.\nIt's a pattern.",
      text: 'Speak up, become a target. Bot attacks, surveillance, smear campaigns.',
      primaryButton: {
        label: 'Watch now',
        url: 'https://live.wsocial.eu/wedonthavetime.wsocial.eu',
        openInNewTab: false,
      },
      secondaryButton: {
        label: 'Close',
        url: null,
      },
    },
  ],
}

export function filterActiveAnnouncements(
  announcements: Announcement[],
  now: number,
): Announcement[] {
  return announcements.filter(
    a =>
      new Date(a.showFrom).getTime() <= now &&
      now <= new Date(a.showUntil).getTime(),
  )
}

async function fetchAnnouncements(): Promise<AnnouncementsConfig | null> {
  if (!ANNOUNCEMENTS_URL) return null
  try {
    const res = await fetch(`${ANNOUNCEMENTS_URL}/config`)
    if (!res.ok) return null
    return res.json()
  } catch {
    return null
  }
}

type AnnouncementsContextValue = {
  queue: Announcement[]
  markShown: (id: string) => void
}

const Context = createContext<AnnouncementsContextValue>({
  queue: [],
  markShown: () => {},
})
Context.displayName = 'AnnouncementsContext'

export function Provider({children}: React.PropsWithChildren<{}>) {
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(
    () => new Set(persisted.get('dismissedAnnouncements') ?? []),
  )
  // ponytail: tick-every-minute re-evaluates the queue so upcoming announcements
  // become visible within ~1 min of their showFrom — no custom setTimeout needed
  const tick = useTickEveryMinute()

  const {data, refetch} = useQuery({
    staleTime: 1000 * 15,
    queryKey: ['announcements'],
    refetchInterval: 1000 * 60 * 5,
    queryFn: fetchAnnouncements,
  })

  useOnAppStateChange(state => {
    if (state === 'active') void refetch()
  })

  const queue = useMemo(() => {
    // Use tick directly as `now` so React Compiler sees a genuine read and
    // keeps the dependency — void-only refs get stripped by the compiler.
    // tick === Date.now() at the last minute interval, accurate enough for announcements.
    const config = ANNOUNCEMENTS_URL ? data : FALLBACK_ANNOUNCEMENTS
    if (!config) return []
    return filterActiveAnnouncements(config.announcements, tick).filter(
      a => !dismissed.has(a.id),
    )
  }, [data, dismissed, tick])

  const markShown = (id: string) => {
    setDismissed(prev => {
      const next = new Set(prev)
      next.add(id)
      void persisted.write('dismissedAnnouncements', [...next])
      return next
    })
  }

  return (
    <Context.Provider value={{queue, markShown}}>{children}</Context.Provider>
  )
}

export async function prefetchAnnouncements(): Promise<void> {
  await fetchAnnouncements()
}

export function useAnnouncementsContext() {
  return useContext(Context)
}
